/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */

/**
 * Internal Imports
 */
import { Dropdown } from '../../../shared/dropdowns';
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import { IPostUpdateEmployeeConfigPayload } from '../../../../redux/actions';
import { useGetSassTokenQuery } from '../../../../redux/apis/Sass.api';
import {
  useGetEmployeeConfigQuery,
  usePostUpdateRoundingListConfigMutation
} from '../../../../redux/apis/Users.api';

import {
  toTitleCase,
  handleBlobPicUrl,
  userProfilePicUrl
} from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps, Employee } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import {
  DropdownOptions,
  EmployeeAvatar,
  EmployeeSlot,
  EmployeeNameInTable,
  ManagerNameInTable,
  Meta,
  RoundingConfigContainer,
  RoundingConfigHeader,
  TextInTable
} from './MyEmployeeListTable.styles';

/**
 * Static File Imports
 */
import defaultImage from '../../../../assets/images/defaultImage.png';

interface MyEmployeesListProps {
  authorizedUser?: AuthUserProps;
}

const MyEmployeeListTable: FC<MyEmployeesListProps> = ({ authorizedUser }) => {
  const [sortKey, setSortKey] = useState<string>('lastName');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Employee');
  const [selectedRoundingFrequency, setSelectedRoundingFrequency] = useState<
    string | undefined
  >(
    authorizedUser?.employeeInfo?.myRoundingListConfig?.listRoundingFrequency ||
      'monthly'
  );

  const [openFreq, setOpenFreq] = useState(false);
  const [mounted, setMounted] = useState<boolean>(false);

  const {
    data: { directEmployees: myEmployeesList = [] } = {},
    // isError: isGetEmployeeConfigError,
    // isFetching: isGetEmployeeConfigFetching,
    isLoading: isGetEmployeeConfigLoading
  } = useGetEmployeeConfigQuery(authorizedUser?.hcaid ?? '', {
    refetchOnMountOrArgChange: 120
  });
  const [
    updateRoundingListConfig
    // {
    //   isError: isUpdateRoundingListConfigError,
    //   isLoading: isUpdateRoundingListConfigLoading,
    //   isSuccess: isUpdateRoundingListConfigSuccess
    // }
  ] = usePostUpdateRoundingListConfigMutation();
  const {
    data: sasTokenData,
    // isError: isGetSassTokenError,
    isFetching: isGetSassTokenFetching
    // isLoading: isGetSassTokenLoading
  } = useGetSassTokenQuery();

  const handleRoundingSelect = (selected: string) => {
    setSelectedRoundingFrequency(selected);
  };

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Employee':
        key = 'lastName';
        break;
      case 'Department':
        key = 'dept';
        break;
      case 'Title':
        key = 'title';
        break;
      case 'Manager':
        key = 'managerFullName';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const sortedList = useMemo(() => {
    const stateCopy = [...myEmployeesList];

    stateCopy.sort((a: Employee, b: Employee) =>
      a[sortKey as keyof Employee]?.toLowerCase() >
      b[sortKey as keyof Employee]?.toLowerCase()
        ? sortDir === 'asc'
          ? 1
          : -1
        : sortDir === 'asc'
        ? -1
        : 1
    );

    return stateCopy;
  }, [myEmployeesList, sortKey, sortDir]);

  const isTableEmpty = useMemo(() => sortedList.length === 0, [sortedList]);

  useEffect(() => {
    if (mounted) {
      updateRoundingListConfig({
        userId: authorizedUser?.hcaid,
        listRoundingFrequency: selectedRoundingFrequency
      } as IPostUpdateEmployeeConfigPayload);
    } else {
      setMounted(true);
    }
  }, [selectedRoundingFrequency]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Employee"
      id="My-Employee-List-Table-Employee-Column"
      unit="column"
      colWidth={25}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      Employee
    </CellContainer>,
    <CellContainer
      key="Department"
      id="My-Employee-List-Table-Department-Column"
      unit="column"
      colWidth={25}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      Department
    </CellContainer>,
    <CellContainer
      key="Title"
      id="My-Employee-List-Table-Title-Column"
      unit="column"
      colWidth={25}
      direction={!(active === 'Title') ? 'none' : sortDir}
      sort
    >
      Title
    </CellContainer>,
    <CellContainer
      key="Manager"
      id="My-Employee-List-Table-Manager-Column"
      unit="column"
      colWidth={25}
      direction={!(active === 'Manager') ? 'none' : sortDir}
      sort
    >
      Manager
    </CellContainer>,
    <CellContainer
      key="Delete"
      id="Employee-List-Table-Delete-Column"
      unit="column"
      colWidth={12}
      direction="none"
    />
  ];

  const rows = sortedList.map((employee: Employee, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`My-Employee-List-Row-${i}`}
        id={`My-Employee-List-Row-${employee.hcaid}`}
        unit="row"
        size="default"
        height="5em"
      >
        <CellContainer unit="column" colWidth={25}>
          <EmployeeSlot>
            {sasTokenData ? (
              <EmployeeAvatar
                src={
                  handleBlobPicUrl(
                    employee.pictureUrl,
                    sasTokenData.sasToken
                  ) ?? userProfilePicUrl(employee.hcaid, sasTokenData.sasToken)
                }
                alt="Employee Image"
                onError={e => {
                  e.currentTarget.src = defaultImage;
                  return null;
                }}
              />
            ) : (
              <EmployeeAvatar src={defaultImage} alt="Default Image" />
            )}
            <div>
              <EmployeeNameInTable className="my-0">{`${employee.firstName} ${employee.lastName}`}</EmployeeNameInTable>
              <TextInTable className="my-0">{employee.hcaid}</TextInTable>
            </div>
          </EmployeeSlot>
        </CellContainer>
        <CellContainer unit="column" colWidth={25}>
          <Meta>
            <TextInTable className="my-0">{employee.dept}</TextInTable>
          </Meta>
        </CellContainer>
        <CellContainer unit="column" colWidth={25}>
          <Meta>
            <TextInTable className="my-0">{employee.title}</TextInTable>
          </Meta>
        </CellContainer>
        <CellContainer unit="column" colWidth={25}>
          <Meta>
            <div>
              <ManagerNameInTable className="my-0">
                {employee.managerFullName || 'Unknown'}
              </ManagerNameInTable>
              <TextInTable className="my-0">
                {employee.managerHcaid}
              </TextInTable>
            </div>
          </Meta>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <div id="My-Employees-Container" className="d-flex flex-column w-100">
      <RoundingConfigContainer>
        <div>
          <RoundingConfigHeader className="mb-0 pe-3">
            Rounding Frequency
          </RoundingConfigHeader>
          {/* TODO: Convert these to two new components - custom dropdowns */}
          <DropdownOptions>
            <Dropdown
              open={openFreq}
              placeholder={toTitleCase(selectedRoundingFrequency) || ''}
              setOpen={setOpenFreq}
            >
              <div
                aria-selected={selectedRoundingFrequency === 'monthly'}
                className={`dropdown-hover-item${
                  selectedRoundingFrequency === 'monthly' ? ' selected' : ''
                }`}
                role="option"
                tabIndex={0}
                onClick={() => handleRoundingSelect('monthly')}
                onKeyDown={() => handleRoundingSelect('monthly')}
              >
                Monthly
              </div>
              <div
                aria-selected={selectedRoundingFrequency === 'bi-monthly'}
                className={`dropdown-hover-item${
                  selectedRoundingFrequency === 'bi-monthly' ? ' selected' : ''
                }`}
                role="option"
                tabIndex={0}
                onClick={() => handleRoundingSelect('bi-monthly')}
                onKeyDown={() => handleRoundingSelect('bi-monthly')}
              >
                Bi-Monthly
              </div>
              <div
                aria-selected={selectedRoundingFrequency === 'quarterly'}
                className={`dropdown-hover-item${
                  selectedRoundingFrequency === 'quarterly' ? ' selected' : ''
                }`}
                role="option"
                tabIndex={0}
                onClick={() => handleRoundingSelect('quarterly')}
                onKeyDown={() => handleRoundingSelect('quarterly')}
              >
                Quarterly
              </div>
            </Dropdown>
          </DropdownOptions>
        </div>
      </RoundingConfigContainer>
      <Table
        customClass="my-employee-list-table"
        headers={headers}
        rows={rows}
        isTableEmpty={isTableEmpty}
        loading={isGetEmployeeConfigLoading || isGetSassTokenFetching}
        type="my-employee-list"
        onSort={(e: any) => handleSort(e.detail.label)}
      />
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser
  };
};
export default connect(mapReduxStateToProps)(MyEmployeeListTable);
